<template>
  <div class="feedback-wrapper">
    <!-- Floating Action Button -->
    <se-button
      v-if="!hideFab"
      variant="primary"
      style-set="set8"
      class="feedback-fab"
      aria-label="Send feedback"
      @click="openFeedback"
    >
      <v-icon size="24">mdi-message-text-outline</v-icon>
    </se-button>

    <!-- Feedback Modal -->
    <Teleport to="body">
      <div v-if="isOpen" class="feedback-overlay" @click.self="closeFeedback">
        <div
          ref="feedbackDialogRef"
          class="feedback-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="feedback-dialog-title"
          tabindex="-1"
          @keydown.esc="closeFeedback"
          @keydown.tab="trapDialogFocus($event, feedbackDialogRef)"
        >
          <div class="modal-header">
            <h2 id="feedback-dialog-title">Send Feedback</h2>
            <se-button
              variant="text"
              style-set="set8"
              class="close-btn"
              aria-label="Close feedback dialog"
              @click="closeFeedback"
            >
              <v-icon size="20">mdi-close</v-icon>
            </se-button>
          </div>

          <form @submit.prevent="submitFeedback">
            <!-- Screenshot Preview -->
            <div v-if="screenshotUrl" class="screenshot-section">
              <label class="field-label">Screenshot</label>
              <div class="screenshot-preview">
                <img :src="screenshotUrl" alt="Screenshot" />
                <div class="screenshot-actions">
                  <se-button type="button" variant="secondary" style-set="set8" class="screenshot-btn" @click="retakeScreenshot">
                    <v-icon size="14">mdi-refresh</v-icon> Retake
                  </se-button>
                  <se-button type="button" variant="secondary" style-set="set8" class="screenshot-btn remove" @click="removeScreenshot">
                    <v-icon size="14">mdi-delete</v-icon> Remove
                  </se-button>
                </div>
              </div>
            </div>

            <!-- Feedback Type -->
            <div class="field-group">
              <label class="field-label">Feedback Type</label>
              <div class="type-selector">
                <se-button
                  v-for="type in feedbackTypes"
                  :key="type.value"
                  type="button"
                  :variant="feedbackType === type.value ? 'primary' : 'secondary'"
                  style-set="set8"
                  :class="['type-btn', { selected: feedbackType === type.value }]"
                  :aria-pressed="feedbackType === type.value"
                  @click="feedbackType = type.value"
                >
                  <v-icon size="18">{{ type.icon }}</v-icon>
                  <span>{{ type.label }}</span>
                </se-button>
              </div>
            </div>

            <!-- Page URL (auto-captured) -->
            <div class="field-group">
              <form-field
                id="feedback-page"
                type="text"
                label="Page"
                :model-value="pageUrl"
                readonly
              />
            </div>

            <!-- Message -->
            <div class="field-group">
              <form-field
                id="feedback-message"
                v-model="message"
                type="textarea"
                label="Message"
                placeholder="Describe your feedback, bug, or suggestion..."
                :rows="4"
                required
              />
            </div>

            <!-- Email (optional) -->
            <div class="field-group">
              <form-field
                id="feedback-email"
                v-model="email"
                type="text"
                label="Email (optional)"
                placeholder="your@email.com"
                hint="We'll only contact you if we need more information"
              />
            </div>

            <!-- Submit -->
            <div class="modal-footer">
              <se-button type="button" variant="secondary" @click="closeFeedback">
                Cancel
              </se-button>
              <se-button type="submit" variant="primary" :disabled="isSubmitting || !message">
                {{ isSubmitting ? 'Sending...' : 'Send Feedback' }}
              </se-button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref } from 'vue'
// html2canvas is lazy-loaded to reduce initial bundle size
import SeButton from '@/components/SEButton.vue'
import FormField from '@/components/ui/FormField.vue'
import { trapDialogFocus } from '@/utils/dialogFocus'

defineProps({
  hideFab: {
    type: Boolean,
    default: false
  }
})

const isOpen = ref(false)
const isSubmitting = ref(false)
const screenshotUrl = ref<string | null>(null)
const screenshotBlob = ref<Blob | null>(null)
const feedbackType = ref('bug')
const message = ref('')
const email = ref('')
const pageUrl = ref('')
const feedbackDialogRef = ref<HTMLElement | null>(null)
let previouslyFocusedElement: HTMLElement | null = null

const feedbackTypes = [
  { value: 'bug', label: 'Bug', icon: 'mdi-bug-outline' },
  { value: 'suggestion', label: 'Suggestion', icon: 'mdi-lightbulb-outline' },
  { value: 'praise', label: 'Praise', icon: 'mdi-star-outline' },
  { value: 'question', label: 'Question', icon: 'mdi-help-circle-outline' },
]

const captureScreenshot = async () => {
  try {
    // Dynamic import to reduce initial bundle size
    const html2canvas = (await import('html2canvas')).default
    const canvas = await html2canvas(document.body, {
      scale: 0.5,
      logging: false,
      useCORS: true,
      allowTaint: true
    })
    screenshotUrl.value = canvas.toDataURL('image/png')
    canvas.toBlob((blob) => {
      if (blob) screenshotBlob.value = blob
    }, 'image/png')
  } catch (error) {
    console.error('Failed to capture screenshot:', error)
  }
}

const openFeedback = async () => {
  previouslyFocusedElement = document.activeElement instanceof HTMLElement
    ? document.activeElement
    : null
  pageUrl.value = window.location.href
  await captureScreenshot()
  isOpen.value = true
  await nextTick()
  feedbackDialogRef.value?.focus()
}

const closeFeedback = () => {
  isOpen.value = false
  resetForm()
  const focusTarget = previouslyFocusedElement
  previouslyFocusedElement = null
  void nextTick(() => focusTarget?.focus())
}

const resetForm = () => {
  feedbackType.value = 'bug'
  message.value = ''
  email.value = ''
  screenshotUrl.value = null
  screenshotBlob.value = null
}

const retakeScreenshot = async () => {
  isOpen.value = false
  await new Promise(resolve => setTimeout(resolve, 100))
  await captureScreenshot()
  isOpen.value = true
  await nextTick()
  feedbackDialogRef.value?.focus()
}

const removeScreenshot = () => {
  screenshotUrl.value = null
  screenshotBlob.value = null
}

const submitFeedback = async () => {
  if (!message.value.trim()) return

  isSubmitting.value = true

  try {
    const formData = new FormData()
    formData.append('feedbackType', feedbackType.value)
    formData.append('page', pageUrl.value)
    formData.append('message', message.value)
    formData.append('email', email.value || '')
    formData.append('userAgent', navigator.userAgent)
    formData.append('timestamp', new Date().toISOString())

    if (screenshotBlob.value) {
      formData.append('screenshot', screenshotBlob.value, 'screenshot.png')
    }

    const response = await fetch('/api/v1/feedback/submit', {
      method: 'POST',
      body: formData
    })

    // Parse JSON response to check actual success status
    const result = await response.json()

    if (response.ok && result.success) {
      alert('Thank you for your feedback!')
      closeFeedback()
    } else {
      // Use backend error message if available
      throw new Error(result.message || 'Failed to submit feedback')
    }
  } catch (error) {
    console.error('Failed to submit feedback:', error)
    const errorMessage = error instanceof Error ? error.message : 'Failed to send feedback. Please try again.'
    alert(errorMessage)
  } finally {
    isSubmitting.value = false
  }
}

// Expose open method for external triggers (like TrustSection CTA)
defineExpose({ openFeedback })
</script>

<style scoped>
/* FAB Button */
.feedback-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  box-shadow: var(--shadow-accent);
  z-index: var(--z-feedback);
}

.feedback-fab:hover {
  transform: scale(1.05);
  box-shadow: var(--shadow-accent-hover);
}

/* Modal Overlay */
.feedback-overlay {
  position: fixed;
  inset: 0;
  background: var(--overlay-scrim);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-banner);
  padding: 24px;
}

/* Modal */
.feedback-modal {
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-xl);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: var(--shadow-modal);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-subtle);
}

.modal-header h2 {
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.close-btn {
  color: var(--text-secondary);
  padding: 4px;
}

.close-btn:hover {
  color: var(--text-primary);
  background: var(--bg-canvas);
}

/* Form Fields */
.field-group {
  padding: 0 24px;
  margin-bottom: 20px;
}

.field-label {
  display: block;
  font-size: var(--font-size-caption);
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 8px;
}

/* Type Selector */
.type-selector {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.type-btn {
  flex-direction: column;
  padding: 12px 8px;
  border-radius: var(--radius-md);
}

.type-btn span {
  font-size: var(--font-size-micro);
  font-weight: 500;
}

/* Screenshot */
.screenshot-section {
  padding: 0 24px;
  margin-bottom: 20px;
}

.screenshot-preview {
  position: relative;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.screenshot-preview img {
  width: 100%;
  max-height: 150px;
  object-fit: cover;
}

.screenshot-actions {
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: flex;
  gap: 8px;
}

.screenshot-btn {
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  font-size: var(--font-size-xs);
}

.screenshot-btn.remove:hover {
  border-color: var(--state-error);
  color: var(--state-error);
}

/* Footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid var(--border-subtle);
}

/* Mobile responsive */
@media (max-width: 639px) {
  .type-selector {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Hide on mobile to avoid clutter and collision with primary FABs */
@media (max-width: 767px) {
  .feedback-fab {
    display: none !important;
  }
}
</style>
